/**
 * Injection Script
 *
 * This script auto-executes when loaded on a webpage.
 * It evaluates injection rules and conditionally loads scripts based on URL patterns and conditions.
 *
 * SAFETY: Basic error handling to prevent failures from breaking execution.
 * When loaded with <script async>, errors are automatically isolated from the host application.
 */

import { createLogger, onDOMReady, ScriptInjector } from "@/utils";
import { SCRIPT_VERSION } from "@/constants";
import { injectionRules } from "@/config/injection-rules";

// Wrap entire script execution in IIFE with error protection
(() => {
  try {
    const logger = createLogger("InjectionScript");

    /**
     * Main initialization function
     */
    const initialize = async (): Promise<void> => {
      try {
        logger.log("Initializing Injection Script", `v${SCRIPT_VERSION}`);
        logger.log(`Current URL: ${window.location.href}`);
        logger.log(`Current Path: ${window.location.pathname}`);

        // Create script injector instance
        const injector = new ScriptInjector(logger);

        // Process all injection rules
        const results = await injector.processRules(injectionRules);

        // Log summary
        const successful = results.filter((r) => r.success).length;
        const failed = results.filter((r) => !r.success).length;

        logger.log(
          `Injection complete: ${successful} successful, ${failed} failed`,
        );
        logger.log(
          `Injected scripts: ${injector.getInjectedScripts().join(", ") || "none"}`,
        );
      } catch (error) {
        console.error("[InjectionScript] Initialization error:", error);
      }
    };

    /**
     * Auto-execute when script loads
     */
    onDOMReady(() => {
      initialize().catch((error) => {
        console.error("[InjectionScript] Failed to initialize:", error);
      });
    });
  } catch (error) {
    console.error("[InjectionScript] Fatal error:", error);
  }
})();
